// Libraries
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Box } from "@mui/material";

// Types
import { TSearchTransferRequest, TTransferType } from "../../types";

// Components
import { TransferRequestSearch } from "./SearchTransferRequest";
import { TransferRequestTable } from "../../Tables/DealerTrades/TransferRequestTable";
import { usePaginatedResource } from "@/Common/hooks/Pagination";
import { getInventoryTradeVehicles } from "@/DataPages/api/inventory/queries";
import { IPageParam } from "@/Common/api/types/pagination";
import { buildGetNextPageParam } from "@/Common/hooks/Pagination/helpers";
import { IInventoryTradeVehicles } from "../../types/dealerTrades";
import { removeFalsyFilters } from "../../helpers";
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";
import { onToggleDealerTradeBlockByLocation } from "@DataPages/api/inventory/subscriptions.ts";

type TTransferRequestActionProps = {
	table: string;
	locationId: string;
	commissionCycleId?: string;
	payPeriodId: string;
	transferType: TTransferType;
	iconType: TTransferType;
	wrapperClasses?: string;
	locationsOptions: {
		isLoadingLocations: boolean;
		mappedLocations: { value: string; label: string }[];
		franchisesByLocations: { franchises: string[]; locationId: string }[];
	};
	defaultSearchTransferRequest?: Partial<TSearchTransferRequest>;
	disableLocationOptionsAndValidation?: boolean;
	isCreatingTransferRequest?: boolean;
	isCurrentPayPeriod: boolean;
	setTransferType: (value: TTransferType) => void;
	onCreate: (filters: Partial<TSearchTransferRequest>, data?: IInventoryTradeVehicles, cleaner?: () => void) => void;
};

const PAGINATION_SIZE = 100;

const TransferRequestAction = (props: Readonly<TTransferRequestActionProps>) => {
	const {
		locationId,
		commissionCycleId,
		payPeriodId,
		transferType,
		iconType,
		setTransferType,
		onCreate,
		wrapperClasses,
		locationsOptions,
		defaultSearchTransferRequest,
		disableLocationOptionsAndValidation,
		isCreatingTransferRequest,
		isCurrentPayPeriod,
		table,
	} = props;
	const queryClient = useQueryClient();
	const { addSubscription, getSubscription } = useSubscriptions();
	const [searchTransferRequest, setSearchTransferRequest] = useState<Partial<TSearchTransferRequest>>(
		{} as Partial<TSearchTransferRequest>
	);
	const [isSearchDone, setIsSearchDone] = useState(false);

	const {
		data: inventoryTradeVehicles,
		refetch,
		isLoading,
		hasNextPage,
	} = usePaginatedResource({
		queryKey: ["getInventoryTradeVehicles", locationId, table],
		queryFn: async ({ pageParam }) => {
			const typedPageParam = pageParam as IPageParam<string>;
			if (!typedPageParam || !("after" in typedPageParam) || !("first" in typedPageParam)) {
				throw new Error("PageParam should have properties 'after' and 'first'");
			}
			const cleanedFilters = removeFalsyFilters(searchTransferRequest);

			const paginatedResponse = await getInventoryTradeVehicles(locationId, cleanedFilters, {
				first: typedPageParam.first,
				after: typedPageParam.after,
			});
			if (!paginatedResponse) {
				throw new Error("Error retrieving Inventory Trade Vehicles");
			}
			return paginatedResponse;
		},
		getNextPageParam: buildGetNextPageParam(PAGINATION_SIZE),
		enabled: false,
		initialPageParam: { after: "", first: PAGINATION_SIZE },
		staleTime: Infinity,
		gcTime: 5000,
		fetchAllPages: true,
	});

	/********************************************* SUBSCRIPTION ************************************************/
	const dealerTradeBlockSubscriptionInSearch = getSubscription(["toggleDealerTradeBlockByLocationInSearch", locationId]);
	dealerTradeBlockSubscriptionInSearch?.subscriptions?.onToggleDealerTradeBlockByLocationInSearch?.setCallback(() => {
		refetch();
	});

	function clearSearchTransferRequest() {
		if (isCurrentPayPeriod) {
			setSearchTransferRequest({} as TSearchTransferRequest);
			setTransferType("" as TTransferType);
			setIsSearchDone(false);
			queryClient.removeQueries({ queryKey: ["getInventoryTradeVehicles", locationId, table] });
		}
	}

	const searchTransferRequestHandler = () => {
		if (isCurrentPayPeriod) {
			refetch();
			setIsSearchDone(true);
		}
	};

	const handleChangeTransferRequest = (newTransferRequest: Partial<TSearchTransferRequest>) => {
		if (isCurrentPayPeriod) {
			setIsSearchDone(false);
			queryClient.removeQueries({ queryKey: ["getInventoryTradeVehicles", locationId, table] });
			setSearchTransferRequest(newTransferRequest);
		}
	};

	const handleCrate = (data?: IInventoryTradeVehicles) => {
		if (isCurrentPayPeriod) {
			onCreate(searchTransferRequest, data, clearSearchTransferRequest);
		}
	};

	useEffect(() => {
		if (locationId) {
			addSubscription(
				["toggleDealerTradeBlockByLocationInSearch", locationId],
				[
					{
						context: "onToggleDealerTradeBlockByLocationInSearch",
						query: onToggleDealerTradeBlockByLocation,
						variables: {
							locationId,
						},
					},
				]
			);
		}
	}, [locationId]);

	if (transferType === "") {
		return null;
	}

	if (transferType === "Asset Swap") {
		return null;
	}

	return (
		<Box className={wrapperClasses}>
			<TransferRequestSearch
				table={table}
				transferType={transferType}
				locationId={locationId}
				searchTransferRequest={searchTransferRequest}
				locationsOptions={locationsOptions}
				onChangeTransferRequest={handleChangeTransferRequest}
				clearSearchTransferRequest={clearSearchTransferRequest}
				searchTransferRequestHandler={searchTransferRequestHandler}
				defaultSearchTransferRequest={defaultSearchTransferRequest}
				disableLocationOptionsAndValidation={disableLocationOptionsAndValidation}
				isCurrentPayPeriod={isCurrentPayPeriod}
			/>
			{isSearchDone && (
				<TransferRequestTable
					table={table}
					transferType={transferType}
					locationId={locationId}
					commissionCycleId={commissionCycleId}
					payPeriodId={payPeriodId}
					iconType={iconType}
					locationsOptions={locationsOptions}
					inventoryTradeVehicles={inventoryTradeVehicles}
					onCreateTransferRequest={handleCrate}
					isLoadingInventory={isLoading}
					hasNextPage={hasNextPage}
					isCreatingTransferRequest={isCreatingTransferRequest}
					defaultSearchTransferRequest={defaultSearchTransferRequest}
					isCurrentPayPeriod={isCurrentPayPeriod}
				/>
			)}
		</Box>
	);
};

export default TransferRequestAction;
